/**
 * joPlanImport — which columns a BQC1 Prod Plan (JO sheet) import may write.
 *
 * WHAT THIS IS FOR (Mikko, 2026-09-14): "i want to have import Button for
 * BQC1 Prod Plan which is this JO sheet... though i have import button in
 * Production Grid, this time i want to have solo import for BQC1 Prod Plan".
 * The Grid's Import reads the whole consolidated workbook — JO plus BQC2..BQC5
 * — and the planner wants the mother record refreshed on its own, from the one
 * sheet the Fulfillment team hands over, without waiting on the rest.
 *
 * THE DANGER IS NOT DELETION, IT IS NULLS. `bulkImport` never deletes a WIP:
 * it upserts by wipId and leaves anything absent from the incoming set exactly
 * as it stands (see its toCreate/toUpdate split). What a JO-only run CAN do is
 * far quieter. The parser fills every BQC2..BQC5 field from sheets that are
 * not in this file, so they all come back empty, and `wipBody` writes an empty
 * field as `null` — which CLEARS the column outright rather than leaving it
 * alone. Run unguarded against the live list, one JO import would blank
 * currentStage, cutAt, receivedAt, qcPassedAt, the sewer and the GRN on every
 * WIP it touched: 21,616 bundles losing their entire stage history to an
 * import that looked like it only added new orders.
 *
 * So an update from this sheet sends ONLY the columns the sheet owns. Every
 * other key is deleted from the body, and `wipBody` is explicit that this is
 * the difference that matters: "A field absent from a MERGE body is left
 * exactly as SharePoint holds it."
 *
 * A CREATE IS THE OPPOSITE CASE, and that is why there are two sets rather
 * than one. A WIP appearing on the JO sheet for the first time has genuinely
 * not been cut, dispatched, received or QC'd — empty is not a gap in the
 * evidence, it is the truth about a bundle that was handed over this morning.
 * Omitting currentStage on a create would leave a new row with no stage at
 * all, which every screen reads as neither BQC1 nor anything else. So creates
 * write the full body and updates write the narrow one.
 *
 * WK SERIES IS DERIVED, NOT READ, and that is what makes this viable at all:
 * the JO sheet has no WK Series column, and `wkSeriesFor` reconstructs the
 * code from the row's own date (see wkSeries.ts, checked against five live
 * batch codes). A JO row with a real ORDER ID therefore lands in its proper
 * week batch; one without stays Buffer, which is what Buffer means.
 *
 * Pure and I/O-free — the write itself is ProductionGridService.bulkImport.
 */

/**
 * The iSyn_WIPs columns the JO sheet is the author of.
 *
 * Named in SharePoint's spelling, because that is what `wipBody` keys its
 * body with and what `missingFields()` reports. Where the interface key
 * differs (wipId -> wipNumber, batchId -> BatchIDId) both live here, so the
 * one set can be handed to `wipBody` and to `wipChanged` — which compares
 * IProductionWip keys — without either needing a translation table.
 *
 * Title is the list's own key column and always carries the WIP id.
 */
export const JO_PLAN_FIELDS: readonly string[] = [
  "Title",
  "wipNumber", "wipId",
  "orderId",
  "serialId",
  // JO's CUTTER column. NOT cutOperator: the parser feeds both from one
  // variable, but cutOperator is BQC2's record of who actually cut the
  // bundle and a plan must never overwrite it — see the parser's own
  // `label(c2?.extra.cutter) || label(row['CUTTER'])`.
  "cutter",
  "design",
  "sku",
  // Derived from the SKU, which the sheet carries.
  "category",
  "qty",
  "storeChannel",
  "color",
  "size",
  "dueDate",
  "joSeries",
  // Both derived from the row's date rather than read — see wkSeries.ts.
  "wkSeries",
  // Three spellings of one column, because three places name it differently:
  // `BatchID` is the lookup as the list spec declares it, `BatchIDId` the id
  // column wipBody actually writes, and `batchId` the interface key
  // wipChanged compares. Missing one is how a column ends up unclassified.
  "BatchID", "BatchIDId", "batchId",
  "dateCreated",
  "dateAssignedToCutter",
];

/**
 * The iSyn_WIPs columns an UPDATE from this sheet must not touch.
 *
 * Every one of these is written by a stage the JO sheet knows nothing about,
 * and every one would be sent as `null` if it were left in the body. Grouped
 * by who owns it, because the reason to leave a column alone is always "some
 * other screen is the author of it" and that is the thing worth checking when
 * a new column is added.
 */
export const NON_PLAN_WIP_FIELDS: readonly string[] = [
  // The stage itself, and the three per-WIP stamps the live top-up reads to
  // decide whether the workbook has already counted a bundle. Blank these and
  // countedByWorkbook answers "not counted" for everything, which double-counts
  // the entire history on the next render. See liveStageTopUp.
  "currentStage",
  "cutAt",
  "receivedAt",
  "qcPassedAt",
  // BQC2.
  "cutOperator",
  // BQC3 — the app writes AssignedSewer and assignedDS together, so they are
  // protected together, exactly as preserveSewer does it.
  "AssignedSewer", "assignedSewer",
  "assignedDS",
  "dispatchedAt",
  // BQC4.
  "receivedQty",
  "deliveryStatus",
  "grn",
  // BQC5.
  "qcPassedQty",
  "qcPersonnel",
  "qcRepairQty",
  // BQC1's own later markers and the costing pair — real columns on the sheet
  // the hub carries, absent from the handover file, so a JO run has no opinion
  // on them and must not express one.
  "inboundedToRtgStockroom",
  "packed",
  // Read from the workbook's Cancelled tab, which is not in this file: a
  // JO-only parse reads every row as uncancelled and would un-cancel them all.
  "cancellationStatus",
  "directMatl",
  "cogm",
  "notes",
];

/**
 * The columns the STATIONS own, and which no workbook import may overwrite.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE SET ABOVE (Mikko, 2026-09-14: "you know
 * it would compromise the native data of BQCs?"). `NON_PLAN_WIP_FIELDS` is
 * about one file being narrow — the JO handover has no BQC2..BQC5 sheets, so a
 * plan run has no opinion on them. This is about something else entirely: the
 * stations went native on 2026-09-08 and the scheduled sync stopped with them,
 * so for these columns the workbook's opinion is not merely absent, it is
 * WRONG — a snapshot of a file last saved before the work happened.
 *
 * Every one of these is written by a station through its own list now, and a
 * full import UPDATE carrying the workbook's value would roll the bundle back
 * to whenever that file was last saved. On 2026-09-14 that was five days.
 *
 * CREATES ARE UNAFFECTED, for the reason the header gives: a WIP the app has
 * never seen has no native state to protect, and omitting currentStage on a
 * create leaves a row every screen reads as nowhere. This guards UPDATES only.
 *
 * NOT the office columns beside them — cancellationStatus, packed,
 * inboundedToRtgStockroom, directMatl, cogm and notes are still authored in
 * the workbook and a full import is still their only way in. Widening this set
 * to cover them would freeze columns nothing else can write.
 */
export const STATION_OWNED_WIP_FIELDS: readonly string[] = [
  // The stage itself, and the three stamps the live top-up reads.
  "currentStage", "cutAt", "receivedAt", "qcPassedAt",
  // BQC2 · Cutting.
  "cutOperator",
  // BQC3 · Dispatch. AssignedSewer already had the migration valve's
  // protection; it is named here too so the guard does not depend on a
  // setting being read successfully.
  "AssignedSewer", "assignedSewer", "assignedDS", "dispatchedAt",
  // BQC4 · Receiving.
  "receivedQty", "deliveryStatus", "grn",
  // BQC5 · QC.
  "qcPassedQty", "qcPersonnel", "qcRepairQty",
];

/**
 * The omit set for a FULL import's updates — the schema gap, plus everything
 * a station owns.
 *
 * A full import still writes the plan, the office columns, the batch rollups
 * and the stage history. What it no longer does is answer for work it cannot
 * have witnessed.
 */
export function fullImportOmitFields(schemaGap?: ReadonlySet<string>): Set<string> {
  const out = new Set<string>(STATION_OWNED_WIP_FIELDS);
  if (schemaGap) for (const f of schemaGap) out.add(f);
  return out;
}

/**
 * The omit set for an UPDATE — the schema gap this site already has, plus
 * every column the JO sheet does not own.
 *
 * Takes the gap rather than computing it: `bulkImport` reads `missingFields()`
 * once up front and a second probe would be a second answer.
 */
export function planOnlyOmitFields(schemaGap?: ReadonlySet<string>): Set<string> {
  const out = new Set<string>(NON_PLAN_WIP_FIELDS);
  if (schemaGap) for (const f of schemaGap) out.add(f);
  return out;
}

/**
 * Batch-header columns a JO-only run must not write.
 *
 * THE PLAN IMPORT OWNS prodPlan AND NOTHING ELSE ON A BATCH. That column IS
 * the JO sheet — it is the sum of the plan's own quantities, and it is the
 * denominator of every Output % and Pending % on the grid. Leaving it to the
 * full import meant a batch that gained rows carried a stale denominator and
 * every percentage on its row read high (found 2026-09-14), with the only
 * remedy being an import that overwrites the stations. That is not a remedy.
 *
 * The four rollups are the opposite case and the reason this set exists: a JO
 * sheet knows nothing about cut, assigned, received or QC, so parsed from that
 * file alone every total comes back 0 and writing them would zero the rollup
 * on every batch the plan mentions. `batchStatus` goes with them — a plan run
 * has no business reclassifying a batch — and so does `notes`.
 */
export const PLAN_BATCH_OMIT_FIELDS: readonly string[] = [
  "cutTotal", "assignedTotal", "receivedTotal", "qcTotal", "batchStatus", "notes",
];

/** The omit set for a plan run's batch UPDATE: the schema gap, plus the
 *  rollups this file cannot speak for. */
export function planBatchOmitFields(schemaGap?: ReadonlySet<string>): Set<string> {
  const out = new Set<string>(PLAN_BATCH_OMIT_FIELDS);
  if (schemaGap) for (const f of schemaGap) out.add(f);
  return out;
}

/** One JO row, reduced to what a preview needs to judge the run. */
export interface IJoPlanRow {
  wipId: string;
  batchCode?: string;
  dueDate?: string;
  orderId?: string;
}

export interface IJoPlanPreview {
  /** Rows the sheet carries that are not yet in iSyn_WIPs. */
  creating: number;
  /** Rows already in iSyn_WIPs — these get the narrow update body. */
  updating: number;
  /** Distinct batch codes across the whole sheet. */
  batches: number;
  /** Rows with no order behind them, which stay Buffer. */
  buffer: number;
  /** The date span the sheet covers, by due date. */
  dueFrom?: string;
  dueTo?: string;
  /** WIP ids appearing more than once in the file. */
  duplicateWipIds: string[];
}

/**
 * What a run would do, before it does it.
 *
 * Preview-then-write, the same posture as the Migration Valve's own backfills:
 * this writes the table every station reads, so the number of rows it is about
 * to touch belongs in front of a person first. Counting creates separately
 * from updates is the part that matters — "21,616 updates" is a routine
 * refresh and "21,616 creates" means the WIP ids did not match and something
 * is wrong with the file.
 */
export function joPlanPreview(
  rows: readonly IJoPlanRow[],
  existingWipIds: ReadonlySet<string>,
): IJoPlanPreview {
  let creating = 0;
  let updating = 0;
  let buffer = 0;
  const batches = new Set<string>();
  const seen = new Set<string>();
  const dupes = new Set<string>();
  let dueFrom: string | undefined;
  let dueTo: string | undefined;

  for (const r of rows) {
    const wipId = String(r.wipId ?? "").trim();
    if (!wipId) continue;
    if (seen.has(wipId)) dupes.add(wipId);
    else {
      seen.add(wipId);
      if (existingWipIds.has(wipId)) updating++; else creating++;
    }
    if (r.batchCode) batches.add(r.batchCode);
    // Buffer is the absence of a real order, the same signal the parser uses.
    if (!r.orderId || r.orderId.trim() === "" || r.orderId.trim() === "0") buffer++;
    const due = (r.dueDate ?? "").slice(0, 10);
    if (due.length === 10) {
      if (!dueFrom || due < dueFrom) dueFrom = due;
      if (!dueTo || due > dueTo) dueTo = due;
    }
  }

  return {
    creating, updating, buffer,
    batches: batches.size,
    dueFrom, dueTo,
    duplicateWipIds: [...dupes].sort(),
  };
}
